import React, { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';

gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);

const SCROLL_EDGE_THRESHOLD = 24;

const ScrollManager: React.FC = () => {
  const scrollingRef = useRef(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isMobile = () =>
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
    window.innerWidth <= 768;

  useEffect(() => {
    if (isMobile()) return;

    let removeListeners: (() => void) | undefined;

    const initTimeout = setTimeout(() => {
      const sections = [
        document.getElementById('hero'),
        document.getElementById('about'),
        document.getElementById('services'),
        document.getElementById('technologies'),
        document.getElementById('projects'),
        document.getElementById('contact'),
      ].filter(Boolean) as HTMLElement[];

      if (sections.length <= 1) return;

      const getNearestSection = () => {
        const viewportMid = window.innerHeight * 0.35;
        let current = 0;

        sections.forEach((section, index) => {
          const rect = section.getBoundingClientRect();
          if (rect.top <= viewportMid && rect.bottom > viewportMid) {
            current = index;
          }
        });

        return current;
      };

      const canScrollWithinSection = (section: HTMLElement, direction: 1 | -1) => {
        const rect = section.getBoundingClientRect();
        const sectionTallerThanViewport = section.offsetHeight > window.innerHeight + 8;

        if (!sectionTallerThanViewport) return false;

        if (direction > 0) {
          return rect.bottom > window.innerHeight + SCROLL_EDGE_THRESHOLD;
        }

        return rect.top < -SCROLL_EDGE_THRESHOLD;
      };

      const scrollToSection = (index: number) => {
        if (index < 0 || index >= sections.length) return;

        scrollingRef.current = true;
        gsap.killTweensOf(window);

        const targetSection = sections[index];
        const anchor = targetSection.querySelector(`#${targetSection.id}-anchor`);
        const targetElement = anchor || targetSection;

        gsap.to(window, {
          duration: 0.85,
          scrollTo: {
            y: targetElement,
            offsetY: 0,
            autoKill: true,
          },
          ease: 'power1.out',
          onComplete: () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
            timeoutRef.current = setTimeout(() => {
              scrollingRef.current = false;
            }, 200);
          },
        });
      };

      const handleWheel = (event: WheelEvent) => {
        if (scrollingRef.current) {
          event.preventDefault();
          return;
        }

        const currentIndex = getNearestSection();
        const currentSection = sections[currentIndex];
        const direction = (event.deltaY > 0 ? 1 : -1) as 1 | -1;

        if (canScrollWithinSection(currentSection, direction)) {
          return;
        }

        const targetIndex = currentIndex + direction;
        if (targetIndex >= 0 && targetIndex < sections.length) {
          event.preventDefault();
          scrollToSection(targetIndex);
        }
      };

      const handleKeyDown = (event: KeyboardEvent) => {
        if (scrollingRef.current) return;

        const currentIndex = getNearestSection();
        const currentSection = sections[currentIndex];
        let direction: 1 | -1 | 0 = 0;

        if (event.key === 'ArrowDown' || event.key === 'PageDown') direction = 1;
        else if (event.key === 'ArrowUp' || event.key === 'PageUp') direction = -1;
        else return;

        if (canScrollWithinSection(currentSection, direction)) {
          return;
        }

        const targetIndex = currentIndex + direction;
        if (targetIndex >= 0 && targetIndex < sections.length) {
          event.preventDefault();
          scrollToSection(targetIndex);
        }
      };

      window.addEventListener('wheel', handleWheel, { passive: false });
      window.addEventListener('keydown', handleKeyDown);

      removeListeners = () => {
        window.removeEventListener('wheel', handleWheel);
        window.removeEventListener('keydown', handleKeyDown);
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
      };
    }, 500);

    return () => {
      clearTimeout(initTimeout);
      removeListeners?.();
    };
  }, []);

  useEffect(() => {
    if (isMobile()) return;

    const handleNavClick = () => {
      setTimeout(() => {
        scrollingRef.current = false;
      }, 300);
    };

    const navButtons = document.querySelectorAll('nav button');
    navButtons.forEach((button) => {
      button.addEventListener('click', handleNavClick);
    });

    return () => {
      navButtons.forEach((button) => {
        button.removeEventListener('click', handleNavClick);
      });
    };
  }, []);

  return null;
};

export default ScrollManager;
